/**
 * shop.ts — all spend-money actions: buildings, workers, upgrades, tool tiers,
 * and unlocking deeper layers. Pure functions returning success booleans.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { bulkCost, maxAffordable } from '../core/numbers';
import {
  BUILDING_BY_ID,
  WORKER_BY_ID,
  UPGRADE_BY_ID,
  TOOL_BY_LEVEL,
  ORE_BY_DEPTH,
  MAX_TOOL_LEVEL,
  MAX_DEPTH,
} from '../data';
import { meetsRequirement } from './unlock';

// --- Buildings ---
export function buildingCost(state: GameState, id: string, count = 1): number {
  const b = BUILDING_BY_ID[id];
  if (!b) return Infinity;
  return bulkCost(b.baseCost, b.growth, state.buildings[id] ?? 0, count);
}
export function buildingUnlocked(state: GameState, id: string): boolean {
  const b = BUILDING_BY_ID[id];
  return !!b && meetsRequirement(state, b.requires);
}
export function maxBuildings(state: GameState, id: string): number {
  const b = BUILDING_BY_ID[id];
  if (!b) return 0;
  return maxAffordable(state.economy.money, b.baseCost, b.growth, state.buildings[id] ?? 0);
}
export function buyBuilding(state: GameState, id: string, count = 1, bus?: EventBus): boolean {
  if (!buildingUnlocked(state, id) || count <= 0) return false;
  const cost = buildingCost(state, id, count);
  if (state.economy.money < cost) return false;
  state.economy.money -= cost;
  state.buildings[id] = (state.buildings[id] ?? 0) + count;
  bus?.emit('building:bought', { buildingId: id, count });
  return true;
}

// --- Workers ---
export function workerCost(state: GameState, id: string, count = 1): number {
  const w = WORKER_BY_ID[id];
  if (!w) return Infinity;
  return bulkCost(w.baseCost, w.growth, state.workers[id] ?? 0, count);
}
export function workerUnlocked(state: GameState, id: string): boolean {
  const w = WORKER_BY_ID[id];
  return !!w && meetsRequirement(state, w.requires);
}
export function maxWorkers(state: GameState, id: string): number {
  const w = WORKER_BY_ID[id];
  if (!w) return 0;
  return maxAffordable(state.economy.money, w.baseCost, w.growth, state.workers[id] ?? 0);
}
export function hireWorker(state: GameState, id: string, count = 1, bus?: EventBus): boolean {
  if (!workerUnlocked(state, id) || count <= 0) return false;
  const cost = workerCost(state, id, count);
  if (state.economy.money < cost) return false;
  state.economy.money -= cost;
  state.workers[id] = (state.workers[id] ?? 0) + count;
  bus?.emit('worker:hired', { workerId: id, count });
  return true;
}

// --- Upgrades (one-time) ---
export function upgradeUnlocked(state: GameState, id: string): boolean {
  const u = UPGRADE_BY_ID[id];
  return !!u && !state.upgrades[id] && meetsRequirement(state, u.requires);
}
export function buyUpgrade(state: GameState, id: string, bus?: EventBus): boolean {
  const u = UPGRADE_BY_ID[id];
  if (!u || state.upgrades[id] || !meetsRequirement(state, u.requires)) return false;
  if (state.economy.money < u.cost) return false;
  state.economy.money -= u.cost;
  state.upgrades[id] = true;
  bus?.emit('upgrade:bought', { upgradeId: id });
  return true;
}

// --- Tool tiers ---
export function nextToolCost(state: GameState): number | null {
  if (state.mining.toolLevel >= MAX_TOOL_LEVEL) return null;
  return TOOL_BY_LEVEL[state.mining.toolLevel + 1]?.cost ?? null;
}
export function upgradeTool(state: GameState, bus?: EventBus): boolean {
  const cost = nextToolCost(state);
  if (cost === null || state.economy.money < cost) return false;
  state.economy.money -= cost;
  state.mining.toolLevel += 1;
  bus?.emit('upgrade:bought', { upgradeId: `tool${state.mining.toolLevel}` });
  return true;
}

// --- Depth ---
export function nextDepthCost(state: GameState): number | null {
  const target = state.mining.maxDepth + 1;
  if (target > MAX_DEPTH) return null;
  return ORE_BY_DEPTH[target]?.unlockCost ?? null;
}
/** Deeper layers need both money and a deep-enough elevator shaft. */
export function canUnlockDepth(state: GameState): boolean {
  const target = state.mining.maxDepth + 1;
  if (target > MAX_DEPTH) return false;
  const cost = nextDepthCost(state);
  if (cost === null || state.economy.money < cost) return false;
  return (state.buildings['elevator'] ?? 0) >= target;
}
export function unlockNextDepth(state: GameState, bus?: EventBus): boolean {
  if (!canUnlockDepth(state)) return false;
  const target = state.mining.maxDepth + 1;
  const cost = nextDepthCost(state)!;
  state.economy.money -= cost;
  state.mining.maxDepth = target;
  state.mining.focusDepth = target;
  const ore = ORE_BY_DEPTH[target]!;
  bus?.emit('layer:unlocked', { depth: target });
  bus?.emit('ore:unlocked', { oreId: ore.id });
  bus?.emit('notify', { kind: 'success', message: `${ore.name} in Tiefe ${target} freigeschaltet!` });
  return true;
}
export function setFocus(state: GameState, depth: number): boolean {
  if (depth < 0 || depth > state.mining.maxDepth) return false;
  state.mining.focusDepth = depth;
  return true;
}
